'use client'

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

interface FavoritesContextType {
  favorites: string[]
  addFavorite: (elementId: string) => void
  removeFavorite: (elementId: string) => void
  toggleFavorite: (elementId: string) => void
  isFavorite: (elementId: string) => boolean
  clearFavorites: () => void
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined)

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<string[]>([])
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
    const storedFavorites = localStorage.getItem('elementium_favorites')
    if (storedFavorites) {
      try {
        setFavorites(JSON.parse(storedFavorites))
      } catch (e) {
        console.error('Error loading favorites:', e)
      }
    }
  }, [])

  useEffect(() => {
    if (isClient) {
      localStorage.setItem('elementium_favorites', JSON.stringify(favorites))
    }
  }, [favorites, isClient])

  const addFavorite = useCallback((elementId: string) => {
    setFavorites(prev => {
      if (prev.includes(elementId)) return prev
      return [...prev, elementId]
    })
  }, [])

  const removeFavorite = useCallback((elementId: string) => {
    setFavorites(prev => prev.filter(id => id !== elementId))
  }, [])

  const toggleFavorite = useCallback((elementId: string) => {
    setFavorites(prev => {
      if (prev.includes(elementId)) {
        return prev.filter(id => id !== elementId)
      }
      return [...prev, elementId]
    })
  }, [])

  const isFavorite = useCallback((elementId: string) => {
    return favorites.includes(elementId)
  }, [favorites])

  const clearFavorites = useCallback(() => {
    setFavorites([])
  }, [])

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        addFavorite,
        removeFavorite,
        toggleFavorite,
        isFavorite,
        clearFavorites
      }}
    >
      {children}
    </FavoritesContext.Provider>
  )
}

export function useFavorites() {
  const context = useContext(FavoritesContext)
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider')
  }
  return context
}
